import React, { useEffect } from "react";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import type { FieldControlSize } from "../../../../lib/field-control-size";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import { useSelector } from "react-redux";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { store } from "../../../../store";


interface InputFieldProps {
  label: string;
  name: string;
  value: string | number;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  maxLength?: number;
  step?: string;
  placeholder?: string;
  pattern?: RegExp;
  patternMsg?: string;
  tooltipText?: React.ReactNode;
  onChange: (name: string, value: string) => void;
  onBlur?: (e: any) => void;
  handleDisabled?: (name: string, defaultValue: any) => void;
  defaultValue?: any;
  autoFocus?: boolean;
  onKeyDown?: (e: any) => void;
  controlSize?: FieldControlSize;
}

export const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  value,
  type = "text",
  required = false,
  disabled = false,
  min,
  max,
  maxLength,
  step,
  placeholder,
  pattern,
  patternMsg,
  tooltipText,
  onChange,
  onBlur,
  handleDisabled,
  defaultValue,
  autoFocus,    
  onKeyDown,
  controlSize,
}) => {
  const dispatch = useStoreDispatch();
  const errorMsg = useSelector(errorMsgSelector);
  const error = errorMsg?.[name] || "";
  const TEXT_SANITIZE_RE = /(<([^>]+)>)|<>/gi;
  /* Reset to default when disabled (mirrors old handleDisabled) */
  useEffect(() => {
    if (handleDisabled && disabled) {
      handleDisabled(name, defaultValue);
    }
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- inline validation (mirrors old InputField.handleInput) ---- */
  const validate = (raw: string) => {
    const existing = store.getState().helpers.errorMsg;
    // clear previous error for this field first
    const cleared: Record<string, string> = {};
    for (const [k, v] of Object.entries(existing)) {
      if (k !== name) cleared[k] = String(v ?? "");
    }
    dispatch(setErrorMsg(cleared));

    // number type check
    if (type === "number" && raw && isNaN(Number(raw))) {
      dispatch(
        setErrorMsg({
          ...existing,
          [name]: "Please enter a number.",
        })
      );
      return;
    }

    // range check
    if (min !== undefined || max !== undefined) {
      const num = parseInt(raw, 10);
      if (
        (!isNaN(num) && min !== undefined && num < min) ||
        (!isNaN(num) && max !== undefined && num > max)
      ) {
        dispatch(
          setErrorMsg({
            ...existing,
            [name]: `Invalid input. Please enter a value between ${min} and ${max}.`,
          })
        );
        return;
      }
    }

    // pattern check
    if (pattern && raw && !pattern.test(raw)) {
      dispatch(
        setErrorMsg({
          ...existing,
          [name]: patternMsg || `Invalid input. Please enter a valid ${name}`,
        })
      );
    }
  };

  /* ---- onBlur: required‑field validation (mirrors validateInput) ---- */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;

    // badInput check (e.g. "e" in number field) takes highest priority
    if (type === "number" && (e.target as HTMLInputElement).validity?.badInput) {
      dispatch(
        setErrorMsg({
          ...store.getState().helpers.errorMsg,
          [name]: "Please enter a number.",
        })
      );
      return;
    }

    // Pattern check takes first priority
    if (pattern && val && !pattern.test(val)) {
      dispatch(
        setErrorMsg({
          ...store.getState().helpers.errorMsg,
          [name]: patternMsg || `Invalid input. Please enter a valid ${name}`,
        })
      );
      return;
    }

    // Range check
    if (type === "number" && val && (min !== undefined || max !== undefined)) {
      const num = Number(val);
      if (
        (!isNaN(num) && min !== undefined && num < min) ||
        (!isNaN(num) && max !== undefined && num > max)
      ) {
        dispatch(
          setErrorMsg({
            ...store.getState().helpers.errorMsg,
            [name]: `Invalid input. Please enter a value between ${min} and ${max}.`,
          })
        );
        return;
      }
    }

    // Required check
    if (required && !val) {
      dispatch(
        setErrorMsg({ ...errorMsg, [name]: `${label} is required!.` })
      );
      return;
    }

    // Only call parent onBlur if no pattern error exists
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let val = e.target.value;
    // detect invalid number input (e.g. typing "e", "+", "-" alone)
    if (type === "number" && (e.target as HTMLInputElement).validity?.badInput) {
      const existing = store.getState().helpers.errorMsg;
      dispatch(
        setErrorMsg({
          ...existing,
          [name]: "Please enter a number.",
        })
      );
      onChange(name, val);
      return;
    }
    // sanitize text type (same regex as old InputField)
    if (type === "text") {
      val = val.replace(TEXT_SANITIZE_RE, "");
    }
    validate(val);
    onChange(name, val);
  };

  const field = (
    <div className="flex flex-col gap-2 w-full" role="group" data-slot="field" data-field-size={controlSize}>
      <Label htmlFor={name} className="text-sm font-medium mb-3">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      <Input
        id={name}
        name={name}
        type={type}
        value={String(value ?? "")}
        disabled={disabled}
        required={required}
        min={min}
        max={max}
        maxLength={maxLength}
        step={step}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        className={error ? "!border-destructive focus:!ring-destructive" : ""}
        autoFocus={autoFocus}
      />
      {error && (
        <p className="text-xs text-destructive m-0">{error}</p>
      )}
    </div>
  );

  if (!tooltipText) return field;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{field}</TooltipTrigger>
        <TooltipContent side="top" align="end" sideOffset={-17} className="z-[1400]">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};